import { isArray, some, isPlainObject, find } from 'lodash-es';

/**
 * Recursively checks if an object or array contains any key that includes a specific substring.
 * It works for objects, arrays of objects, and deeply nested structures.
 *
 * @param {Object|Array} obj - The object or array to search through.
 * @param {string} substring - The substring to look for within the keys.
 * @returns {boolean} Returns `true` if any key contains the substring; otherwise, `false`.
 */

export const containsKeyWithSubstring = (obj, substring) => {
    try {
        // If the input is an array, iterate over its elements
        if (isArray(obj)) {
            return some(obj, item => {
                // Recursively check each item in the array
                if (isPlainObject(item) || isArray(item)) {
                    return containsKeyWithSubstring(item, substring);
                }
                return false;
            });
        }

        // If the input is a plain object, iterate over its properties
        if (isPlainObject(obj)) {
            return some(obj, (value, key) => {
                // Check if the current key includes the specified substring
                if (key.includes(substring)) {
                    return true;
                }

                // If the value is an array, check each element recursively
                if (isArray(value)) {
                    return some(value, item => {
                        // Ensure the item is an object or array before recursion
                        if (isPlainObject(item) || isArray(item)) {
                            return containsKeyWithSubstring(item, substring);
                        }
                        return false;
                    });
                }

                // If the value is a plain object, check its properties recursively
                if (isPlainObject(value)) {
                    return containsKeyWithSubstring(value, substring);
                }

                // For other data types (e.g., strings, numbers), return false
                return false;
            });
        }

        // Return false if the input is neither an array nor an object
        return false;
    } catch (error) {
        console.log(`containsKeyWithSubstring: ${error?.message} ${JSON.stringify(obj, 2, null)}`);
        return false;
    }
};

/**
 * Recursively searches through an object or array and returns
 * a list of all objects that contain at least one key including
 * any of the specified substrings.
 *
 * It supports deeply nested structures (arrays and objects).
 *
 * @param {Object|Array} obj - The object or array to traverse.
 * @param {string[]} substrings - An array of substrings to look for in keys.
 * @returns {Object[]} An array of objects containing at least one matching key.
 *
 * @example
 * const results = findAllObjectsWithKeyContainingSubstrings(data, ['ij_amount', 'extra_details']);
 */
export const findAllObjectsWithKeyContainingSubstrings = (obj, substrings) => {
    const matches = [];

    const search = (node) => {
        if (Array.isArray(node)) {
            for (const item of node) {
                if (isPlainObject(item) || Array.isArray(item)) {
                    search(item);
                }
            }
        } else if (isPlainObject(node)) {
            let matched = false;

            for (const key of Object.keys(node)) {
                if (substrings.some(sub => key.includes(sub))) {
                    matched = true;
                    break;
                }
            }

            if (matched) {
                matches.push(node);
            }

            for (const value of Object.values(node)) {
                if (isPlainObject(value) || Array.isArray(value)) {
                    search(value);
                }
            }
        }
    };

    if (Array.isArray(substrings)) {
        search(obj);
    }

    return matches;
};

/**
 * Builds the services and extra details summary from an offer object.
 *
 * This function searches for all coverages in the given offer that include either
 * "ij_amount" or "extra_details" keys. For each coverage, it collects:
 *   - Service amounts and labels from "ij_amount"
 *   - Numeric "extra_details" (excluding those with value "0" for certain coverages)
 *
 * Special rules:
 * - For coverages "met_prev_pro_ipt" and "met_prev_fam_hd_ipt":
 *     - Add the extra_details field "montant_capital_ipt"
 *     - Do NOT include any extra_detail where value === "0"
 * - For coverages "met_prev_pro_dc" and "met_prev_fam_hd_dcne":
 *     - The label should be the detail label only (no "pour la garantie" text)
 * - For all other cases:
 *     - Labels include the coverage name
 *
 * @param {Object} offer The offer object containing coverages and their details.
 * @returns {Object|null} An object with two arrays:
 *   - services: [{ amount: Number, label: String }]
 *   - extra_details: [{
 *       type: String,
 *       value: Number,
 *       label: String,
 *       sequence: Number,
 *       code: String,
 *       coverageCode: String
 *     }]
 *   Returns null if no relevant coverages are found.
 */
export const buildServicesData = offer => {
    const keys = ['ij_amount', 'extra_details'];

    // Find all coverage objects that contain at least one of the specified keys
    const coverages = findAllObjectsWithKeyContainingSubstrings(offer, keys);
    if (!coverages?.length) return null;

    const services = [];
    const extraDetailsList = [];

    // Lists of coverage codes with special display rules
    const rule1Codes = ['met_prev_pro_dc', 'met_prev_fam_hd_dc'];
    const rule2Codes = ['met_prev_pro_ipt', 'met_prev_fam_hd_ipt'];
    const rule3Codes = ['met_prev_pro_ipp', 'met_prev_fam_hd_ipp'];

    coverages.forEach(coverage => {
        const { coverage: covInfo, ij_amount, extra_details } = coverage || {};
        const coverageCode = covInfo?.code;
        const coverageName = covInfo?.name;

        // If ij_amount exists, add it to services with the coverage name in the label
        if (ij_amount) {
            services.push({
                amount: parseFloat(ij_amount.amount),
                label: `${ij_amount.label} pour la garantie ${coverageName}`
            });
        }

        // If extra_details is an object, process each numeric detail
        if (extra_details && typeof extra_details === 'object') {
            const sortedDetails = Object.values(extra_details)
                .filter(
                    detail => detail &&
                    typeof detail === 'object' &&
                    detail.type === 'numeric' &&
                    detail.value !== undefined &&
                    detail.sequence < 1000)
                .sort((a, b) => a.sequence - b.sequence);

            sortedDetails.forEach(detail => {
                    const isRule1 = rule1Codes.includes(coverageCode) && detail.code === 'revenu_global_a_proteger';
                    const isRule2 = rule2Codes.includes(coverageCode);
                    const isRule3 = rule3Codes.includes(coverageCode);

                    const isZeroValue = detail.value === '0';

                    // For rule 2 coverages: skip extra_details whose value is "0"
                    if (isRule2 && isZeroValue) return;

                    // For rule 3 coverages: skip extra_details
                    if (isRule3) return;

                    // Build the label based on the rules
                    const label =
                        isRule1
                            ? detail.label // For rule 1: label is just the detail's label
                            : `${detail.label} pour la garantie ${coverageName}`; // Otherwise, include the coverage name

                    // Add to services
                    services.push({
                        amount: parseFloat(detail.value),
                        code: detail.code,
                        coverageCode,
                        label
                    });

                    // Add to extra_details list
                    extraDetailsList.push({
                        type: detail.type,
                        value: parseFloat(detail.value),
                        label: detail.label,
                        sequence: detail.sequence,
                        code: detail.code,
                        coverageCode
                    });

            });
        }
    });

    // Return the formatted summary
    return { services, extra_details: extraDetailsList };
};
